import { createSlice, type PayloadAction } from "@reduxjs/toolkit/react"
import { SearchFiltersData } from "./SearchFilters"
import apiSlice from "./api/apiSlice"
import _ from "lodash"
import ProfessionalTypeKey from "mco-keys/src/t4/ProfessionalTypeKey"
import { ApiTag } from "./api/util/ApiTag"
import { ToggleColumns } from "./SearchFilters"

export type McoSortingRule = {
	id: string
	desc?: boolean
}

const initialSearchState = {
	page: 0,
	pageSize: 10,
	searchText: "",
	filters: undefined as SearchFiltersData | undefined,
	toggleColumns: {} as ToggleColumns,
	sorted: [
		{
			id: "email",
			desc: false
		}
	] as McoSortingRule[]
}

const initialState = {
	patients: {
		...initialSearchState,
		toggleColumns: {
			"PC UUID": false,
			"Patient UUID": false,
			"CHI Number": false,
			"NHS Number": false,
			"Generic Ireland ID": false,
			"Spell Number": false,
			"Date Of Birth": false,
			"PC Created Date": false
		} as ToggleColumns
	},
	professionals: {
		...initialSearchState,
		toggleColumns: {
			"User Type": true,
			"Treatment Centres": true,
			"GMC Number": true
		} as ToggleColumns
	},
	bulkShareFrom: initialSearchState,
	bulkShareTo: initialSearchState
}

export type FilterId = keyof typeof initialState

const textSearchFilterCodeName: Record<FilterId, string> = {
	patients: "DEFINITION.FILTER.PMA.SEARCH",
	professionals: "DEFINITION.FILTER.DOCTOR.SEARCH",
	bulkShareFrom: "DEFINITION.FILTER.DOCTOR.SEARCH",
	bulkShareTo: "DEFINITION.FILTER.DOCTOR.SEARCH"
}

const updateSelectedFilter = (
	state: typeof initialState,
	{
		payload: { id, filterPipeline, checked }
	}: PayloadAction<{
		id: FilterId
		filterPipeline: string
		checked: boolean
	}>
) => {
	if (!state[id].filters) {
		throw Error("Attempt to update filters when no filters set")
	}

	state[id].filters.categories = state[id].filters.categories.map(
		(category) => {
			const updatedCategory = { ...category }
			updatedCategory.selectors = updatedCategory.selectors.map(
				(selector) => {
					if (selector.filterPipeline !== filterPipeline) {
						return selector
					}
					const updatedFilter = { ...selector }
					updatedFilter.selected = checked
					return updatedFilter
				}
			)
			return updatedCategory
		}
	)
	state[id].page = 0
}

const searchSlice = createSlice({
	name: "search",
	initialState: initialState,
	reducers: {
		initialiseFilters: (
			state,
			{
				payload: { id, filters }
			}: PayloadAction<{
				id: keyof typeof initialState
				filters: SearchFiltersData
			}>
		) => {
			state[id].filters = filters
		},
		setSearchQuery: (
			state,
			{
				payload: { id, searchText }
			}: PayloadAction<{ id: FilterId; searchText: string }>
		) => {
			state[id].searchText = searchText
			state[id].page = 0
		},
		setPageIndex: (
			state,
			{
				payload: { id, pageIndex }
			}: PayloadAction<{ id: FilterId; pageIndex: number }>
		) => {
			state[id].page = pageIndex
		},
		setPageSize: (
			state,
			{
				payload: { id, pageSize }
			}: PayloadAction<{ id: FilterId; pageSize: number }>
		) => {
			state[id].pageSize = pageSize
			state[id].page = 0
		},
		setToggleColumns: (
			state,
			{
				payload: { id, toggleColumns }
			}: PayloadAction<{
				id: FilterId
				toggleColumns: ToggleColumns
			}>
		) => {
			state[id].toggleColumns = toggleColumns
		},
		setSorted: (
			state,
			{
				payload: { id, sorted }
			}: PayloadAction<{
				id: FilterId
				sorted: McoSortingRule[]
			}>
		) => {
			state[id].sorted = sorted
		},
		updateSelectedFilter
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				(action) =>
					apiSlice.util.invalidateTags.type === action.type &&
					action.payload.includes(ApiTag.Tenant),
				() => initialState
			)
			.addMatcher(
				apiSlice.endpoints.patientFilters.matchFulfilled,
				(state, { payload }) => {
					state.patients.filters = payload
				}
			)
			.addMatcher(
				apiSlice.endpoints.professionalFilters.matchFulfilled,
				(state, { payload }) => {
					state.professionals.filters = payload
					state.bulkShareFrom.filters = payload
					const updatedState = _.cloneDeep(state)
					updateSelectedFilter(updatedState, {
						type: "",
						payload: {
							id: "bulkShareFrom",
							filterPipeline: `DEFINITION.FILTER.DOCTOR.USERTYPE:${ProfessionalTypeKey.Consultant}`,
							checked: true
						}
					})
					state.bulkShareFrom.filters =
						updatedState.bulkShareFrom.filters
					state.bulkShareTo.filters = payload
				}
			)
	},
	selectors: {
		state: (slice, id: FilterId) => slice[id],
		searchText: (slice, id: FilterId) => slice[id].searchText,
		filterPipeline: (slice, id: FilterId) => {
			const { filters, searchText: searchQuery } = slice[id]

			if (!filters || !filters.categories || !filters.primaryFilter) {
				return undefined
			}

			const mergedSelectedFilters = filters.categories
				.map((category) => {
					return category.selectors.filter((selector) => {
						return selector.selected
					})
				})
				.filter(
					(selectedFilterCategory) =>
						selectedFilterCategory.length > 0
				)
				.flat()
			const selectedFiltersPipelines = mergedSelectedFilters.map(
				(selectedFilter) => selectedFilter.filterPipeline
			)

			const filterPipeline =
				selectedFiltersPipelines.length > 0
					? `${
							filters.primaryFilter.codeName
						}|${selectedFiltersPipelines.join("|")}`
					: filters.primaryFilter.codeName

			return searchQuery
				? `${filterPipeline}|${textSearchFilterCodeName[id]}:'${searchQuery}'`
				: filterPipeline
		}
	}
})

export default searchSlice
